import { useEffect, useState, useRef } from 'react'
import s from './styles.module.scss'
import { head } from 'helpers'
import { Alert, Layouts, Loader } from 'components'
import * as api from 'api'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST


export default function Main() {
  const [loading, setLoading] = useState(false)
  const [news, setNews] = useState([])
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [processing, setProcessing] = useState(false)
  const processingRef = useRef(false)

  useEffect(() => {
    head({ title: 'Pending News Approval | BCN Admin' })
    fetchPendingNews()
  }, [])

  const fetchPendingNews = async () => {
    if (processingRef.current) return
    processingRef.current = true
    setLoading(true)

    try {
      const res = await api.admin.News.getPendingAggregated({ limit: 100 })
      if (res.code === 200 && res.payload?.news) {
        setNews(res.payload.news)
      } else {
        Alert.error(res.message || 'Failed to load pending news')
      }
    } catch (error) {
      console.error('Error fetching pending news:', error)
      Alert.error('Failed to load pending news')
    }

    setLoading(false)
    processingRef.current = false
  }

  const handleSelect = id => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedIds.size === news.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(news.map(n => n._id)))
    }
  }

  const handleApprove = async () => {
    if (selectedIds.size === 0) {
      Alert.warn('Please select at least one news item')
      return
    }

    if (processingRef.current || processing) return
    processingRef.current = true
    setProcessing(true)

    try {
      const res = await api.admin.News.approveAggregated({ ids: Array.from(selectedIds) })
      if (res.code === 200) {
        Alert.success(`Approved ${res.payload?.approved || selectedIds.size} news item(s)`)
        setSelectedIds(new Set())
        fetchPendingNews()
      } else {
        Alert.error(res.message || 'Failed to approve news')
      }
    } catch (error) {
      console.error('Error approving news:', error)
      Alert.error('Failed to approve news')
    }

    setProcessing(false)
    processingRef.current = false
  }

  const handleReject = async () => {
    if (selectedIds.size === 0) {
      Alert.warn('Please select at least one news item')
      return
    }

    if (processingRef.current || processing) return
    processingRef.current = true
    setProcessing(true)

    try {
      const res = await api.admin.News.rejectAggregated({ ids: Array.from(selectedIds) })
      if (res.code === 200) {
        Alert.success(`Rejected ${res.payload?.rejected || selectedIds.size} news item(s)`)
        setSelectedIds(new Set())
        fetchPendingNews()
      } else {
        Alert.error(res.message || 'Failed to reject news')
      }
    } catch (error) {
      console.error('Error rejecting news:', error)
      Alert.error('Failed to reject news')
    }

    setProcessing(false)
    processingRef.current = false
  }

  if (loading) {
    return (
      <div className={s.main}>
        <Loader />
      </div>
    )
  }

  return (
    <div className={s.main}>
      <Layouts.Classic title='Pending News Approval' />
      <div className={s.content}>
        <div className={s.header}>
          <div className={s.headerInfo}>
            <span className={s.count}>{news.length} pending news item(s)</span>
          </div>
          <div className={s.headerActions}>
            {news.length > 0 && (
              <>
                <button
                  className={s.selectAllButton}
                  onClick={handleSelectAll}
                >
                  {selectedIds.size === news.length ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  className={s.approveButton}
                  onClick={handleApprove}
                  disabled={selectedIds.size === 0 || processing}
                >
                  {processing ? 'Processing...' : `Approve (${selectedIds.size})`}
                </button>
                <button
                  className={s.rejectButton}
                  onClick={handleReject}
                  disabled={selectedIds.size === 0 || processing}
                >
                  {processing ? 'Processing...' : `Reject (${selectedIds.size})`}
                </button>
              </>
            )}
          </div>
        </div>

        {news.length === 0 ? (
          <div className={s.emptyState}>
            <p>No pending news items. All aggregated news has been reviewed.</p>
          </div>
        ) : (
          <div className={s.newsList}>
            {news.map(item => (
              <div
                key={item._id}
                className={`${s.newsCard} ${selectedIds.has(item._id) ? s.selected : ''}`}
                onClick={() => handleSelect(item._id)}
              >
                <div className={s.checkbox}>
                  <input
                    type='checkbox'
                    checked={selectedIds.has(item._id)}
                    onChange={() => handleSelect(item._id)}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                <div className={s.newsContent}>
                  {item.image && (
                    <div className={s.imageContainer}>
                      <img
                        src={IMAGE_HOST + item.image}
                        alt={item.title}
                        onError={e => {
                          e.target.style.display = 'none'
                        }}
                      />
                    </div>
                  )}
                  <div className={s.textContent}>
                    <h3 className={s.title}>{item.title}</h3>
                    <p className={s.description}>{item.description}</p>
                    <div className={s.meta}>
                      <span className={s.author}>
                        {item.authorName}
                        {item.authorTitle && ` - ${item.authorTitle}`}
                      </span>
                      <span className={s.date}>
                        {item.publishedAt
                          ? new Date(item.publishedAt).toLocaleDateString()
                          : new Date(item.createdAt).toLocaleDateString()}
                      </span>
                      {item.sourceUrl && (
                        <a
                          href={item.sourceUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className={s.sourceLink}
                          onClick={e => e.stopPropagation()}
                        >
                          View Source
                        </a>
                      )}
                    </div>
                    {item.keywords && item.keywords.length > 0 && (
                      <div className={s.keywords}>
                        {item.keywords.map((keyword, idx) => (
                          <span key={idx} className={s.keyword}>
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

